import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  matchFunds, resolveFundCbam,
  type FeBreakdownRow, type FeMappingRow, type FeSplitOverride, type PortfolioFund,
} from "./feMatching";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

type Status = "will-change" | "unchanged" | "skipped-unresolved" | "skipped-unmapped" | "no-fe-match" | "zero";

interface FundRow {
  fund: PortfolioFund;
  status: Status;
  reason?: string;
  current: { sector_id: string; sector_name: string; weighting: number }[];
  proposed: { sector_id: string; sector_name: string; weighting: number }[];
  feTotalPct?: number; // raw FE breakdown sum (~100 for normal; <100 = derivatives/cash; >100 = gearing)
}

export default function CbamSyncReviewDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["cbam-sync-review", open],
    enabled: open,
    queryFn: async () => {
      const [fundsRes, feRes, mapRes, ovRes, secRes, curRes] = await Promise.all([
        supabase.from("ba_cbam_funds").select("id, fund_name, isin, fund_manager, fund_class"),
        supabase.from("fe_asset_breakdown").select("id, isin, fund_name, as_of_date, breakdown").order("as_of_date", { ascending: false }),
        supabase.from("fe_sector_mapping").select("*").order("display_order"),
        supabase.from("fe_fund_split_overrides").select("*"),
        supabase.from("ba_master_sectors").select("id, name"),
        supabase.from("ba_cbam_fund_sector_mappings").select("fund_id, sector_id, weighting, clarification_number"),
      ]);
      for (const r of [fundsRes, feRes, mapRes, ovRes, secRes, curRes]) if (r.error) throw r.error;

      // Latest FE row per ISIN
      const feLatest = new Map<string, FeBreakdownRow>();
      for (const r of (feRes.data ?? []) as any[]) {
        if (!feLatest.has(r.isin)) feLatest.set(r.isin, { ...r, breakdown: r.breakdown ?? {} });
      }
      return {
        funds: (fundsRes.data ?? []) as PortfolioFund[],
        feRows: Array.from(feLatest.values()),
        mapping: (mapRes.data ?? []) as FeMappingRow[],
        overrides: (ovRes.data ?? []) as FeSplitOverride[],
        sectors: (secRes.data ?? []) as { id: string; name: string }[],
        current: (curRes.data ?? []) as { fund_id: string; sector_id: string; weighting: number; clarification_number: number }[],
      };
    },
  });

  const rows: FundRow[] = useMemo(() => {
    if (!data) return [];
    const sectorById = new Map(data.sectors.map((s) => [s.id, s.name]));
    const sectorIdByName = new Map(data.sectors.map((s) => [s.name, s.id]));
    const mappingByCol = new Map(data.mapping.map((m) => [m.fe_column_name, m]));
    const overridesByIsinCol = new Map<string, FeSplitOverride[]>();
    for (const o of data.overrides) {
      const k = `${o.isin}::${o.fe_column_name}`;
      if (!overridesByIsinCol.has(k)) overridesByIsinCol.set(k, []);
      overridesByIsinCol.get(k)!.push(o);
    }
    const currentByFund = new Map<string, { sector_id: string; sector_name: string; weighting: number }[]>();
    for (const c of data.current) {
      const arr = currentByFund.get(c.fund_id) ?? [];
      arr.push({ sector_id: c.sector_id, sector_name: sectorById.get(c.sector_id) ?? "?", weighting: Number(c.weighting) });
      currentByFund.set(c.fund_id, arr);
    }
    const matches = matchFunds(data.funds, data.feRows);
    const out: FundRow[] = [];
    for (const m of matches) {
      const current = (currentByFund.get(m.fund.id) ?? []).sort((a, b) => b.weighting - a.weighting);
      if (!m.fe) {
        out.push({ fund: m.fund, status: "no-fe-match", current, proposed: [] });
        continue;
      }
      const priorAlloc: Record<string, number> = {};
      for (const c of current) priorAlloc[c.sector_name] = (priorAlloc[c.sector_name] ?? 0) + c.weighting;
      const res = resolveFundCbam(m.fe, mappingByCol, overridesByIsinCol, priorAlloc);
      if (res.unresolved.length) {
        out.push({ fund: m.fund, status: "skipped-unresolved", reason: "needs split", current, proposed: [] });
        continue;
      }
      const total = Object.values(res.allocation).reduce((s, v) => s + v, 0);
      if (total <= 0) {
        out.push({ fund: m.fund, status: "zero", current, proposed: [] });
        continue;
      }
      const proposed: FundRow["proposed"] = [];
      let unmapped: string | null = null;
      // Keep raw FE percentages — do NOT rescale. Funds whose breakdown sums
      // to ≠100 (derivatives/cash/gearing) are flagged so the net allocation
      // and implied beta are visible.
      for (const [name, pct] of Object.entries(res.allocation)) {
        const id = sectorIdByName.get(name);
        if (!id) { unmapped = name; break; }
        proposed.push({ sector_id: id, sector_name: name, weighting: Number((pct / 100).toFixed(6)) });
      }
      if (unmapped) {
        out.push({ fund: m.fund, status: "skipped-unmapped", reason: `no sector "${unmapped}"`, current, proposed: [] });
        continue;
      }
      proposed.sort((a, b) => b.weighting - a.weighting);
      // Diff
      const changed = current.length !== proposed.length
        || proposed.some((p) => {
          const c = current.find((x) => x.sector_id === p.sector_id);
          return !c || Math.abs(c.weighting - p.weighting) > 0.0005;
        });
      out.push({ fund: m.fund, status: changed ? "will-change" : "unchanged", current, proposed, feTotalPct: total });
    }
    out.sort((a, b) => {
      const rank = (s: Status) => s === "will-change" ? 0 : s === "skipped-unresolved" ? 1 : s === "skipped-unmapped" ? 2 : s === "no-fe-match" ? 3 : s === "zero" ? 4 : 5;
      return rank(a.status) - rank(b.status) || a.fund.fund_name.localeCompare(b.fund.fund_name);
    });
    return out;
  }, [data]);

  // Initialise selection: pre-check only will-change rows
  useEffect(() => {
    if (!rows.length) return;
    setSelected(new Set(rows.filter((r) => r.status === "will-change").map((r) => r.fund.id)));
  }, [rows]);

  const counts = useMemo(() => {
    const c = { willChange: 0, unchanged: 0, skipped: 0, noMatch: 0 };
    for (const r of rows) {
      if (r.status === "will-change") c.willChange++;
      else if (r.status === "unchanged") c.unchanged++;
      else if (r.status === "skipped-unresolved" || r.status === "skipped-unmapped" || r.status === "zero") c.skipped++;
      else if (r.status === "no-fe-match") c.noMatch++;
    }
    return c;
  }, [rows]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const commit = useMutation({
    mutationFn: async () => {
      if (!data) return { synced: 0, skipped: 0 };
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id ?? null;

      const runRes = await supabase.from("fe_cbam_sync_runs")
        .insert({ side: "all", triggered_by: userId, status: "running" })
        .select("id").single();
      if (runRes.error) throw runRes.error;
      const runId = runRes.data.id as string;

      let synced = 0;
      const skippedDetails: { fund_id: string; fund_name: string; reason: string }[] = [];
      const beforeState: any[] = [];
      const afterState: any[] = [];
      const targets = rows.filter((r) => selected.has(r.fund.id) && r.proposed.length > 0);

      try {
        for (const r of targets) {
          const insertRows = r.proposed.map((p, i) => ({
            fund_id: r.fund.id, sector_id: p.sector_id, weighting: p.weighting, clarification_number: i + 1,
          }));
          const prior = r.current.map((c) => ({ sector_id: c.sector_id, weighting: c.weighting }));
          const snap = await supabase.from("ba_cbam_fund_sector_mappings_snapshots").insert({
            fund_id: r.fund.id, created_by: userId, reason: "FE→CBAM sync (review)", sync_run_id: runId, mappings: prior,
          });
          if (snap.error) throw snap.error;
          const del = await supabase.from("ba_cbam_fund_sector_mappings").delete().eq("fund_id", r.fund.id);
          if (del.error) throw del.error;
          const ins = await supabase.from("ba_cbam_fund_sector_mappings").insert(insertRows);
          if (ins.error) throw ins.error;
          beforeState.push({ fund_id: r.fund.id, fund_name: r.fund.fund_name, mappings: prior });
          afterState.push({ fund_id: r.fund.id, fund_name: r.fund.fund_name, mappings: insertRows });
          synced++;
        }
        // Record any skipped (in-review) funds the user left out for visibility
        for (const r of rows) {
          if (selected.has(r.fund.id)) continue;
          if (r.status === "skipped-unresolved" || r.status === "skipped-unmapped" || r.status === "zero" || r.status === "no-fe-match") {
            skippedDetails.push({ fund_id: r.fund.id, fund_name: r.fund.fund_name, reason: r.reason ?? r.status });
          }
        }
        await supabase.from("fe_cbam_sync_runs").update({
          finished_at: new Date().toISOString(),
          synced_count: synced, skipped_count: skippedDetails.length,
          skipped_details: skippedDetails, before_state: beforeState, after_state: afterState,
          status: "success",
        }).eq("id", runId);
        return { synced, skipped: skippedDetails.length };
      } catch (e: any) {
        await supabase.from("fe_cbam_sync_runs").update({
          finished_at: new Date().toISOString(), status: "error", error_message: e?.message ?? String(e),
          synced_count: synced, skipped_count: skippedDetails.length,
          before_state: beforeState, after_state: afterState, skipped_details: skippedDetails,
        }).eq("id", runId);
        throw e;
      }
    },
    onSuccess: ({ synced, skipped }) => {
      qc.invalidateQueries({ queryKey: ["ba-fund-sector-mappings"] });
      qc.invalidateQueries({ queryKey: ["cbam-sync-review"] });
      toast.success(`Synced ${synced} fund${synced === 1 ? "" : "s"}${skipped ? `, ${skipped} skipped` : ""}`);
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
    onSettled: () => setIsSubmitting(false),
  });

  const handleConfirm = () => {
    setIsSubmitting(true);
    commit.mutate();
  };

  const statusBadge = (s: Status) => {
    if (s === "will-change") return <Badge className="text-[10px]">Will change</Badge>;
    if (s === "unchanged") return <Badge variant="outline" className="text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" />Unchanged</Badge>;
    if (s === "skipped-unresolved") return <Badge variant="destructive" className="text-[10px]"><AlertTriangle className="h-3 w-3 mr-1" />Needs split</Badge>;
    if (s === "skipped-unmapped") return <Badge variant="destructive" className="text-[10px]"><AlertTriangle className="h-3 w-3 mr-1" />Unmapped</Badge>;
    if (s === "no-fe-match") return <Badge variant="secondary" className="text-[10px]">No FE match</Badge>;
    return <Badge variant="secondary" className="text-[10px]">Zero alloc</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isSubmitting) onOpenChange(v); }}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Review FE → CBAM Sector Sync</DialogTitle>
          <DialogDescription>
            All funds across every portfolio. Review proposed changes, deselect any you want to exclude, then confirm.
          </DialogDescription>
        </DialogHeader>

        {isLoading || !data ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="flex gap-4 text-xs text-muted-foreground border rounded-md p-3">
              <span><strong className="text-foreground">{counts.willChange}</strong> will change</span>
              <span><strong className="text-foreground">{counts.unchanged}</strong> unchanged</span>
              <span><strong className="text-foreground">{counts.skipped}</strong> skipped</span>
              <span><strong className="text-foreground">{counts.noMatch}</strong> no FE match</span>
              <span className="ml-auto"><strong className="text-foreground">{selected.size}</strong> selected to sync</span>
            </div>

            <div className="max-h-[55vh] overflow-y-auto border rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 w-8"></th>
                    <th className="text-left p-2 min-w-[220px]">Fund</th>
                    <th className="text-left p-2 min-w-[100px]">Status</th>
                    <th className="text-left p-2">Current mapping</th>
                    <th className="text-left p-2">Proposed mapping</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => {
                    const selectable = r.proposed.length > 0;
                    return (
                      <tr key={r.fund.id} className={cn("border-b align-top", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                        <td className="p-2">
                          <Checkbox
                            checked={selected.has(r.fund.id)}
                            disabled={!selectable}
                            onCheckedChange={() => toggle(r.fund.id)}
                          />
                        </td>
                        <td className="p-2">
                          <div className="font-medium">
                            {r.fund.fund_name}
                            {r.fund.fund_class ? <span className="text-muted-foreground"> {r.fund.fund_class}</span> : null}
                          </div>
                          <div className="text-muted-foreground text-[10px]">
                            {r.fund.isin ?? "no ISIN"}{r.fund.fund_manager ? ` · ${r.fund.fund_manager}` : ""}
                          </div>
                        </td>
                        <td className="p-2">
                          {statusBadge(r.status)}
                          {r.reason && <div className="text-[10px] text-muted-foreground mt-1">{r.reason}</div>}
                          {r.feTotalPct !== undefined && Math.abs(r.feTotalPct - 100) > 0.5 && (
                            <Badge
                              variant="outline"
                              className="text-[10px] mt-1 border-amber-500 text-amber-700 dark:text-amber-400"
                              title="FE breakdown does not sum to 100% — likely derivatives, cash drag, or gearing. Values left at raw FE figures to reflect net allocation / implied beta."
                            >
                              {r.feTotalPct > 100 ? "Gearing" : "Derivatives/cash"} ({r.feTotalPct.toFixed(1)}%)
                            </Badge>
                          )}
                        </td>
                        <td className="p-2">
                          {r.current.length === 0 ? <span className="text-muted-foreground italic">—</span> : (
                            <div className="flex flex-wrap gap-1">
                              {r.current.map((c) => (
                                <Badge key={c.sector_id} variant="outline" className="text-[10px]">
                                  {c.sector_name}: {(c.weighting * 100).toFixed(1)}%
                                </Badge>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="p-2">
                          {r.proposed.length === 0 ? <span className="text-muted-foreground italic">—</span> : (
                            <div className="flex flex-wrap gap-1">
                              {r.proposed.map((p) => {
                                const cur = r.current.find((x) => x.sector_id === p.sector_id);
                                const delta = cur ? (p.weighting - cur.weighting) * 100 : p.weighting * 100;
                                const isNew = !cur;
                                return (
                                  <Badge
                                    key={p.sector_id}
                                    variant={isNew ? "default" : Math.abs(delta) > 0.05 ? "secondary" : "outline"}
                                    className="text-[10px]"
                                  >
                                    {p.sector_name}: {(p.weighting * 100).toFixed(1)}%
                                    {r.status === "will-change" && cur && Math.abs(delta) > 0.05 && (
                                      <span className={cn("ml-1", delta > 0 ? "text-emerald-600" : "text-red-600")}>
                                        ({delta > 0 ? "+" : ""}{delta.toFixed(1)})
                                      </span>
                                    )}
                                    {r.status === "will-change" && isNew && <span className="ml-1 text-emerald-600">(new)</span>}
                                  </Badge>
                                );
                              })}
                              {r.current.filter((c) => !r.proposed.find((p) => p.sector_id === c.sector_id)).map((c) => (
                                <Badge key={c.sector_id} variant="destructive" className="text-[10px]">
                                  {c.sector_name}: removed
                                </Badge>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={isSubmitting || selected.size === 0}>
            {isSubmitting && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
            Confirm sync ({selected.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
